import { useEffect, useState } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Select, MenuItem, FormControl, InputLabel,
  TextField, Divider, Paper
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import api from '../api/api';
import { servicesCatalog } from '../config/formsConfig';

const STATUS_LABELS = {
  pending:   { label: 'Pendiente',  color: '#E7A432', bg: '#FFF8EC' },
  reviewing: { label: 'En revisión', color: '#185FA5', bg: '#E6F1FB' },
  assigned:  { label: 'Asignada',   color: '#0F6E56', bg: '#E1F5EE' },
  completed: { label: 'Completada', color: '#27500A', bg: '#EAF3DE' },
  cancelled: { label: 'Cancelada',  color: '#A32D2D', bg: '#FCEBEB' },
};

const getServiceName = (key) =>
  servicesCatalog.find(s => s.slug === key)?.name || key;

const formatFieldLabel = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const renderValue = (val) => {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'Sí' : 'No';
  if (Array.isArray(val)) return val.join(', ');
  if (typeof val === 'object') {
    return Object.entries(val).map(([k, v]) => `${formatFieldLabel(k)}: ${v}`).join(' · ');
  }
  return String(val);
};

export default function AdminRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [snackMsg, setSnackMsg] = useState(null);
  const [selectionModel, setSelectionModel] = useState({ type: 'include', ids: new Set() });

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/api/service-requests', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar las solicitudes.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const token = localStorage.getItem('token');
      await api.patch(`/api/service-requests/${id}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
      if (selected?.id === id) setSelected(prev => ({ ...prev, status }));
    } catch (err) {
      setError('Error al actualizar el estado.');
    }
  };

  const filtered = requests.filter(r => {
    const matchSearch =
      r.user_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.user_email?.toLowerCase().includes(search.toLowerCase()) ||
      getServiceName(r.service_key).toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter ? r.status === statusFilter : true;
    return matchSearch && matchStatus;
  });

  const columns = [
    {
      field: 'created_at', headerName: 'Fecha', width: 120,
      renderCell: (p) => new Date(p.value).toLocaleDateString('es-AR')
    },
    {
      field: 'service_key', headerName: 'Servicio', flex: 1,
      renderCell: (p) => getServiceName(p.value)
    },
    { field: 'user_name', headerName: 'Usuario', flex: 1 },
    { field: 'user_email', headerName: 'Email', flex: 1.2 },
    {
      field: 'status', headerName: 'Estado', width: 140,
      renderCell: (p) => {
        const s = STATUS_LABELS[p.value] || STATUS_LABELS.pending;
        return (
          <Chip label={s.label} size="small"
            sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, fontSize: '0.75rem' }} />
        );
      }
    },
    {
      field: 'actions', headerName: 'Detalle', width: 100, sortable: false,
      renderCell: (p) => (
        <Button size="small" variant="outlined"
          sx={{ fontSize: '0.72rem', borderColor: '#163244', color: '#163244' }}
          onClick={() => setSelected(p.row)}>
          Ver
        </Button>
      )
    }
  ];

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarExport csvOptions={{ fileName: 'solicitudes_fixo' }} />
    </GridToolbarContainer>
  );

  // Parsea los datos JSON de la solicitud seleccionada
  const parseData = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return {}; } }
    return raw;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3, color: '#163244' }}>
        Solicitudes de servicios
      </Typography>

      {/* Filtros */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          label="Buscar usuario o servicio"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small" sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={statusFilter} label="Estado"
            onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={fetchRequests}
          sx={{ backgroundColor: '#163244', color: '#fff', ml: 'auto' }}>
          Actualizar
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Tabla */}
      <DataGrid
        rows={filtered}
        columns={columns}
        autoHeight
        loading={loading}
        getRowId={(r) => r.id}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={setSelectionModel}
        slots={{ toolbar: CustomToolbar }}
        sx={{
          borderRadius: 2,
          '& .MuiDataGrid-columnHeaders': { backgroundColor: '#163244', color: '#fff' },
          '& .MuiDataGrid-row:hover': { backgroundColor: '#f0f4f8' },
        }}
        localeText={{ noRowsLabel: 'No hay solicitudes' }}
      />

      {/* Dialog de detalle */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        {selected && (() => {
          const common   = parseData(selected.common_data);
          const specific = parseData(selected.specific_data);
          const s = STATUS_LABELS[selected.status] || STATUS_LABELS.pending;

          return (
            <>
              <DialogTitle sx={{ backgroundColor: '#163244', color: '#fff',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{getServiceName(selected.service_key)}</span>
                <Chip label={s.label} size="small"
                  sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600 }} />
              </DialogTitle>

              <DialogContent sx={{ pt: 3 }}>

                {/* Datos del usuario */}
                <Typography variant="subtitle2" sx={{ color: '#163244', textTransform: 'uppercase',
                  letterSpacing: 1, fontSize: '0.72rem', mb: 1.5 }}>
                  Usuario
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                  {[
                    ['Nombre', selected.user_name],
                    ['Email', selected.user_email],
                    ['Teléfono', selected.user_phone || '—'],
                    ['Fecha', new Date(selected.created_at).toLocaleString('es-AR')],
                  ].map(([label, value]) => (
                    <Box key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2">{value}</Typography>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Detalles específicos del servicio */}
                {Object.keys(specific).length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ color: '#163244', textTransform: 'uppercase',
                      letterSpacing: 1, fontSize: '0.72rem', mb: 1.5 }}>
                      Detalles del servicio
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                      {Object.entries(specific).map(([key, val]) => (
                        <Paper key={key} elevation={0}
                          sx={{ p: 1.5, backgroundColor: '#f5f8ff', borderRadius: 2, minWidth: 160 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatFieldLabel(key)}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.3 }}>
                            {renderValue(val)}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                  </>
                )}

                {/* Información general (common) */}
                <Typography variant="subtitle2" sx={{ color: '#163244', textTransform: 'uppercase',
                  letterSpacing: 1, fontSize: '0.72rem', mb: 1.5 }}>
                  Información general
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {Object.entries(common).map(([key, val]) => (
                    <Paper key={key} elevation={0}
                      sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2,
                        minWidth: key === 'descripcion' ? '100%' : 160,
                        flexGrow: key === 'descripcion' ? 1 : 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatFieldLabel(key)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.3 }}>
                        {renderValue(val)}
                      </Typography>
                    </Paper>
                  ))}
                </Box>

                {/* Cambio de estado */}
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ color: '#163244' }}>
                    Cambiar estado:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <Select
                      value={selected.status}
                      onChange={(e) => handleStatusChange(selected.id, e.target.value)}
                    >
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <MenuItem key={k} value={k}>{v.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setSelected(null)} variant="outlined"
                  sx={{ borderColor: '#163244', color: '#163244' }}>
                  Cerrar
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
}